import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { DropDownList, LookupDialogDimensions } from '@core/interfaces';
import { EventService, ListService, fromList } from '@core/services';
import { dialogAttributes } from '@util/dialog';

import { endpoints } from '@environment';

import { CustomerDetailRequest, CustomerDetailResponseRow, CustomerLookupRequest, CustomerLookupResponseBody } from '#types/customer';

import { AuthService } from '@auth/services';
import { AuthActions } from '@auth/state';
import { CONSTANTS } from '@customer/constants';
import { CustomerLookupDialogComponent } from '@customer/customer-lookup-dialog/customer-lookup-dialog.component';
import { CustomerActions } from '@customer/state';

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  customerDictionaryFieldListRequestLoading$ = new Subject<boolean>();
  customerItemUomListRequestLoading$ = new Subject<boolean>();
  customerListsLoading$ = new Subject<boolean>();
  customerLookupRequestLoading$ = new Subject<boolean>();
  customerDetailRequestLoading$ = new Subject<boolean>();

  customerLookupDialogOnlyAuthorized$ = new BehaviorSubject<boolean>(true);

  customerLookupDialogDimensions$ = new BehaviorSubject<LookupDialogDimensions>({
    container: { width: 0, height: 0 },
    grid: { width: 0, height: 0 },
  });

  customerList$ = this.store.select(fromList.selectCustomerList);
  authorizedCustomerList$ = this.store.select(fromList.selectCustomerAuthorizedList);
  customerStatusList$ = this.store.select(fromList.selectCustomerStatusList);

  customerItemUomList$ = (custid: string, item: string) => this.store.select(fromList.selectCustomerItemUomList(custid, item));

  customerDictionaryList$ = (custid: string) => this.store.select(fromList.selectCustomerDictionaryList(custid));

  customerDropdownList$ = new BehaviorSubject<DropDownList>(null);
  authorizedCustomerDropdownList$ = new BehaviorSubject<DropDownList>(null);
  customerStatusDropdownList$ = new BehaviorSubject<DropDownList>(null);

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private listService: ListService,
    private store: Store,
    private translocoService: TranslocoService,
    private dialogService: DialogService,
    private eventService: EventService
  ) {
    this.translocoService
      .selectTranslation()
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.authService.authenticated$.pipe(filter((authenticated) => authenticated)).subscribe(() => {
          this.initializeDropdownLists();
        });
      });

    this.authService.custid$.pipe(distinctUntilChanged()).subscribe((custid) => {
      this.requestDictionaryFieldList(custid);
    });
  }

  getCustomerLookup(payload: CustomerLookupRequest) {
    return this.http.post(endpoints.customer.customerLookup, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.GRID_CUSTOMER_LOOKUP_DIALOG),
      map((response: CustomerLookupResponseBody) => response)
    );
  }

  getCustomerDetail(payload: CustomerDetailRequest) {
    return this.http.post(endpoints.customer.detail.detail, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_DETAIL),
      map((response: CustomerDetailResponseRow) => response)
    );
  }

  requestLists() {
    this.store.dispatch(CustomerActions.RequestLists());
  }

  requestDictionaryFieldList(custid: string) {
    if (custid) {
      this.store.dispatch(CustomerActions.RequestCustomerDictionaryFieldList({ custid }));
    }
  }

  requestCustomerItemUomList(custid: string, item: string) {
    if (custid && item) {
      this.store.dispatch(CustomerActions.RequestCustomerItemUomList({ custid, item }));
    }
  }

  initializeDropdownLists() {
    this.customerList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.customerDropdownList$.next(
        this.listService.getDropdownList(list, {
          key1: 'key',
          key2: 'value',
          header1: this.translate('core.services.list.dropdown.header.custid'),
          header2: this.translate('core.services.list.dropdown.header.name'),
        })
      );
    });

    this.authorizedCustomerList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.authorizedCustomerDropdownList$.next(
        this.listService.getDropdownList(list, {
          key1: 'key',
          key2: 'value',
          header1: this.translate('core.services.list.dropdown.header.custid'),
          header2: this.translate('core.services.list.dropdown.header.name'),
        })
      );
    });

    this.customerStatusList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.customerStatusDropdownList$.next(
        this.listService.getDropdownList(list, {
          key1: 'key',
          key2: 'value',
          header1: this.translate('core.services.list.dropdown.header.code'),
          header2: this.translate('core.services.list.dropdown.header.description'),
        })
      );
    });
  }

  customerLookupDialog(onlyAuthorized: boolean = true, config: DynamicDialogConfig = {}): DynamicDialogRef {
    this.customerLookupDialogOnlyAuthorized$.next(onlyAuthorized);

    return this.dialogService.open(CustomerLookupDialogComponent, {
      ...dialogAttributes,
      header: this.translate('customer.customer_lookup_dialog.dialog.title'),
      styleClass: 'lookup-dialog',
      ...config,
    });
  }

  changeCustomer() {
    this.authService.allcusts$.pipe(take(1)).subscribe((allcusts) => {
      if (allcusts === 'N') {
        return;
      }

      const lookupDialogRef = this.customerLookupDialog();

      lookupDialogRef.onClose.pipe(take(1)).subscribe((result) => {
        if (result !== undefined && result.custid) {
          this.store.dispatch(AuthActions.ChangeCustomer({ custid: result.custid }));
        }
      });
    });
  }
}
